import axios from "axios";
import { getToken, removeToken } from "./storageController.js";
import { profile } from "context/profile.js";
// API calls
import { refreshAccessToken } from "utils/requests/auth/refreshToken";

let isRedirecting = false;

const SCHOOL_RESTRICTION_CODES = ["SCHOOL_BLOCKED", "SCHOOL_SUSPENDED"];

const handleSchoolRestriction = (response) => {
  if (isRedirecting) return false;
  const errorCode = response?.data?.error_code;
  if (!SCHOOL_RESTRICTION_CODES.includes(errorCode)) return false;
  isRedirecting = true;
  removeToken();
  const reason = encodeURIComponent(response.data.message || "");
  window.location.replace(`/account-restricted?reason=${reason}&code=${errorCode}`);
  return true;
};

const requestPath = (error) => {
  const url = error?.config?.url;
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).pathname || "";
    } catch (e) {
      return "";
    }
  }
  return url.startsWith("/") ? url : `/${url}`;
};

// Endpoints that must never trigger a refresh attempt of their own.
const AUTH_PATHS = ["/account/login", "/account/logout", "/account/refresh-token", "/verify"];

const isAuthPath = (path) => AUTH_PATHS.some((authPath) => path.includes(authPath));

const redirectToLogin = () => {
  isRedirecting = true;
  removeToken();
  window.location.replace("/login");
};

/**
 * Handles a 401 by refreshing the access token once and replaying the original
 * request. Falls back to clearing the session and redirecting to /login.
 * @param {Object} instance - The axios instance that issued the failed request.
 * @param {Object} config - The failed request's axios config.
 * @returns {Promise<Object|null>} The replayed response, or null when not retried.
 */
const handleUnauthorized = async (instance, config) => {
  if (isRedirecting) return null;

  const path = requestPath({ config });
  const alreadyOnLoginPage = typeof window !== "undefined" && window.location.pathname === "/login";

  // Auth endpoints resolve their own 401s (the caller decides what to do), and
  // the login page has nowhere further to redirect to.
  if (isAuthPath(path) || alreadyOnLoginPage) return null;

  // Only one refresh attempt per request.
  if (config?._retry) {
    redirectToLogin();
    return null;
  }

  const refreshed = await refreshAccessToken();

  if (!refreshed.success) {
    redirectToLogin();
    return null;
  }

  config._retry = true;
  config.headers = { ...config.headers, Authorization: `Bearer ${refreshed.accessToken}` };
  return instance.request(config);
};

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 500,
  withCredentials: true,
});

// Add request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken() || profile.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle errors
axiosInstance.interceptors.response.use(
  async (response) => {
    if (handleSchoolRestriction(response)) return response;
    // validateStatus resolves 4xx, so 401s arrive here rather than as errors.
    if (response?.status === 401) {
      const retried = await handleUnauthorized(axiosInstance, response?.config);
      if (retried) return retried;
    }
    return response;
  },
  async (error) => {
    if (error?.response?.status === 401) {
      const retried = await handleUnauthorized(axiosInstance, error?.config);
      if (retried) return retried;
    }
    return Promise.reject(error);
  },
);

export const axiosUploadInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  validateStatus: (status) => status >= 200 && status < 500,
  withCredentials: true,
});

// Add request interceptor to add auth token and handle FormData
axiosUploadInstance.interceptors.request.use(
  (config) => {
    const token = getToken() || profile.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // For FormData, let the browser set Content-Type with boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle errors
axiosUploadInstance.interceptors.response.use(
  async (response) => {
    if (handleSchoolRestriction(response)) return response;
    if (response?.status === 401) {
      const retried = await handleUnauthorized(axiosUploadInstance, response?.config);
      if (retried) return retried;
    }
    return response;
  },
  async (error) => {
    if (error?.response?.status === 401) {
      const retried = await handleUnauthorized(axiosUploadInstance, error?.config);
      if (retried) return retried;
    }
    return Promise.reject(error);
  },
);

// Create a function that returns the axios instances
export const axiosInstanceAuth = () => {
  return {
    axiosInstance,
    axiosUploadInstance,
  };
};

export default axiosInstance;
